import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems, buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { StockDocumentPage } from './StockDocumentPage';

const STOCK_DOCS_API = '/api/stockdocuments';

// #767 Yazdır butonu ikonu — c360 ikon setinde print yok, sayfaya özel küçük SVG.
function PrintIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

// Belge türü konfigürasyonu (variant → liste davranışı).
const VARIANTS = {
  entry: {
    documentType: 1, transfer: false,
    title: 'Stok Girişi', subtitle: 'Depoya yapılan stok giriş belgeleri.', newLabel: '+ Yeni Giriş',
  },
  exit: {
    documentType: 2, transfer: false,
    title: 'Stok Çıkışı', subtitle: 'Depodan yapılan stok çıkış belgeleri.', newLabel: '+ Yeni Çıkış',
  },
  transfer: {
    documentType: 3, transfer: true,
    title: 'Stok Transferi', subtitle: 'Depolar arası stok transfer belgeleri.', newLabel: '+ Yeni Transfer',
  },
};

const STATUS_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'İşlendi' },
];
const statusLabel = (value) => {
  const found = STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : (value === null || value === undefined || value === '' ? '-' : String(value));
};
const statusTone = (value) => (Number(value) === 1 ? 'ok' : 'neutral');

const COLUMN_FILTER_META = {
  documentNo: { type: 'text', odataField: 'DocumentNo' },
  documentDate: { type: 'text', odataField: 'DocumentDate' },
  description: { type: 'text', odataField: 'Description' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['DocumentNo', 'Description'];

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}
function formatNumber(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
}

export function StockDocumentsListPage({ variant = 'entry' }) {
  const cfg = VARIANTS[variant] || VARIANTS.entry;
  usePageMeta(cfg.title, cfg.subtitle);
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [mode, setMode] = useState('list'); // 'list' | 'new' | 'edit'
  const [editId, setEditId] = useState(null); // #767 düzenlenen belge Id'si

  // #764 MENÜ DEĞİŞİNCE LİSTEYE DÖN. /stock-entry, /stock-exit ve /stock-transfer rotalarının üçü de
  // AYNI bileşeni yalnız `variant` prop'u farklı olacak şekilde render eder (AppRoutes). React aynı
  // eleman tipini yeniden MOUNT ETMEZ — yalnız prop'u değiştirir — bu yüzden `mode` state'i menüler
  // arasında TAŞINIYORDU: "Stok Giriş"te Yeni'ye basıp sonra "Stok Çıkış"a tıklayan kullanıcı, liste
  // hiç görünmeden doğrudan öteki türün YENİ KAYIT formuna düşüyordu (belge türünü karıştırma riski;
  // kullanıcı hangi belgeyi açtığını göremiyor). Her tür kendi listesiyle açılır, yeni kayıt o listenin
  // "Yeni" düğmesinden başlar.
  useEffect(() => {
    setMode('list');
    setEditId(null);
  }, [variant]);
  const [warehouses, setWarehouses] = useState([]);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // #767 silinecek (iptal) belge
  const [deleting, setDeleting] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + kritikler. warehouse/route display kolonları
  // row'dan WarehouseId (giriş/çıkış) veya SourceWarehouseId/TargetWarehouseId (transfer) okur
  // (COLUMN_FILTER_META'da yok) → always'te (variant fark etmeksizin hepsi eklenir, fazla-seçim güvenli).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'WarehouseId', 'SourceWarehouseId', 'TargetWarehouseId'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: STOCK_DOCS_API,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    baseFilter: `DocumentType eq ${cfg.documentType}`,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  useEffect(() => {
    fetch('/api/warehouses/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((w) => setWarehouses(unwrapLookupItems(w)))
      .catch(() => {});
  }, []);

  const warehouseLabel = useCallback((id) => {
    if (!id) return '-';
    const w = warehouses.find((x) => x.id === id);
    if (!w) return '-';
    return w.name ? `${w.textId || ''} - ${w.name}`.replace(/^ - /, '') : (w.textId || '-');
  }, [warehouses]);


  // #767.3 Görüntüle: artık BASİT MODAL değil — kayıt giriş ekranının salt-okunur hâli açılır
  // (Fatura/İrsaliye/Sipariş belgelerindeki davranış). Prefill yolu Düzenle ile ortaktır.
  const openView = useCallback((row) => { setEditId(row.id); setMode('view'); }, []);

  // #767 Düzenle → StockDocumentPage'i düzenleme modunda açar (belge iptal+yeniden oluştur akışı).
  const openEdit = useCallback((row) => { setEditId(row.id); setMode('edit'); }, []);

  // #767 Sil = İPTAL: stok belgesinin fiziksel silmesi yok; backend Cancel endpoint'i belgeyi
  // soft-delete edip stok etkisini geri sarar. Onay sonrası çağrılır.
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${STOCK_DOCS_API}/Cancel`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ id: deleteTarget.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || 'Belge iptal edilemedi');
      }
      showToast('success', 'Belge iptal edildi');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      showToast('error', e.message || 'İptal sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTarget]);

  // #767 Yazdır: diğer belgelerde olduğu gibi belge yazdırılabilir. Stok belgesinin ERP baskı
  // şablonu (DocumentTemplate) olmadığından basit bir yazdırma penceresi açılır (başlık + kalemler).
  const handlePrint = useCallback(async (row) => {
    try {
      const res = await fetch(`${STOCK_DOCS_API}/GetById?Id=${row.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Belge alınamadı');
      const body = await res.json();
      const doc = body?.data ?? body;
      const docLines = doc?.lines || [];

      // Ürün etiketleri (id → kod - ad).
      // #767.3 Ürün etiketleri yalnız yazdırma için, o an çekilir (liste artık etiket cache'i tutmuyor —
      // eski görüntüleme modalı kaldırıldı).
      const labels = {};
      const ids = [...new Set(docLines.map((l) => l.productId).filter(Boolean))];
      const missing = ids.filter((id) => !labels[id]);
      if (missing.length) {
        const filter = missing.map((id) => `Id eq ${id}`).join(' or ');
        const purl = `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`;
        const pres = await fetch(purl, { headers: authHeaders() });
        if (pres.ok) getODataRows(await pres.json()).forEach((p) => { labels[p.id] = p.name ? `${p.textId} - ${p.name}` : p.textId; });
      }

      const esc = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      const wh = cfg.transfer
        ? `${warehouseLabel(doc.sourceWarehouseId)} → ${warehouseLabel(doc.targetWarehouseId)}`
        : warehouseLabel(doc.warehouseId);
      const rowsHtml = docLines.map((l, i) => `<tr>
        <td>${l.lineOrder || i + 1}</td>
        <td>${esc(labels[l.productId] || l.productId)}</td>
        <td style="text-align:right">${formatNumber(l.quantity)}</td>
        <td>${esc(l.unitName || '-')}${Number(l.multiplier) !== 1 ? ` (x${esc(l.multiplier)})` : ''}</td>
      </tr>`).join('');
      const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(cfg.title)} — ${esc(doc.documentNo || displayDate(doc.documentDate))}</title>
        <style>
          body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:24px;}
          h1{font-size:18px;margin:0 0 4px;} .meta{font-size:13px;color:#444;margin-bottom:16px;}
          .meta div{margin:2px 0;} table{width:100%;border-collapse:collapse;font-size:13px;}
          th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;} th{background:#f3f4f6;}
        </style></head><body>
        <h1>${esc(cfg.title)}</h1>
        <div class="meta">
          <div><strong>Belge No:</strong> ${esc(doc.documentNo || '-')}</div>
          <div><strong>Tarih:</strong> ${esc(displayDate(doc.documentDate))}</div>
          <div><strong>${cfg.transfer ? 'Güzergah' : 'Depo'}:</strong> ${esc(wh)}</div>
          ${doc.description ? `<div><strong>Açıklama:</strong> ${esc(doc.description)}</div>` : ''}
        </div>
        <table><thead><tr><th>#</th><th>Ürün</th><th style="text-align:right">Miktar</th><th>Birim</th></tr></thead>
        <tbody>${rowsHtml || '<tr><td colspan="4">Kalem yok</td></tr>'}</tbody></table>
        <script>window.onload=function(){window.print();};</script>
        </body></html>`;
      const w = window.open('', '_blank');
      if (!w) { showToast('error', 'Yazdırma penceresi açılamadı (açılır pencere engellendi)'); return; }
      w.document.write(html);
      w.document.close();
    } catch (e) {
      showToast('error', e.message || 'Yazdırma başarısız');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.transfer, cfg.title, warehouseLabel]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('documentNo', {
        header: 'Belge No', meta: { filterType: 'text' },
        cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
      }),
      columnHelper.accessor('documentDate', {
        header: 'Tarih', meta: { filterType: 'text' },
        cell: (info) => displayDate(info.getValue()),
      }),
    ];
    if (cfg.transfer) {
      cols.push(columnHelper.display({
        id: 'route', header: 'Güzergah (Çıkış → Giriş)',
        cell: ({ row }) => `${warehouseLabel(row.original.sourceWarehouseId)} → ${warehouseLabel(row.original.targetWarehouseId)}`,
      }));
    } else {
      cols.push(columnHelper.display({
        id: 'warehouse', header: 'Depo',
        cell: ({ row }) => warehouseLabel(row.original.warehouseId),
      }));
    }
    cols.push(
      columnHelper.accessor('description', {
        header: 'Açıklama', meta: { filterType: 'text' },
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('status', {
        header: 'Durum', meta: { filterType: 'select' },
        cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
      }),
      columnHelper.display({
        id: 'actions', header: 'İşlemler', enableHiding: false,
        cell: ({ row }) => (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
            {/* #767 Diğer belge listeleriyle (İrsaliye/Sipariş) tutarlı: Düzenle / Sil / Yazdır. */}
            {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Yazdır" aria-label="Yazdır" onClick={(e) => { e.stopPropagation(); handlePrint(row.original); }}><PrintIcon /></button>
            {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}><DeleteIcon /></button>}
          </span>
        ),
      }),
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.transfer, warehouseLabel, openView, openEdit, handlePrint, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'documentNo', headerName: 'Belge No' },
        { headerName: 'Tarih', valueGetter: ({ row }) => displayDate(row.documentDate) },
        cfg.transfer
          ? { headerName: 'Güzergah', valueGetter: ({ row }) => `${warehouseLabel(row.sourceWarehouseId)} -> ${warehouseLabel(row.targetWarehouseId)}` }
          : { headerName: 'Depo', valueGetter: ({ row }) => warehouseLabel(row.warehouseId) },
        { headerName: 'Açıklama', valueGetter: ({ row }) => row.description || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `${variant}-belgeleri_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  if (mode === 'new') {
    return (
      <StockDocumentPage
        variant={variant}
        onBack={() => setMode('list')}
        onSaved={() => { setMode('list'); fetchData(); }}
      />
    );
  }

  // #767.3 Görüntüleme: aynı sayfa salt-okunur açılır.
  if (mode === 'view' && editId) {
    return (
      <StockDocumentPage
        variant={variant}
        editId={editId}
        readOnly
        onBack={() => { setMode('list'); setEditId(null); }}
      />
    );
  }

  // #767 Düzenleme: aynı sayfa editId ile açılır (belge iptal edilip yeniden oluşturulur).
  if (mode === 'edit' && editId) {
    return (
      <StockDocumentPage
        variant={variant}
        editId={editId}
        onBack={() => { setMode('list'); setEditId(null); }}
        onSaved={() => { setMode('list'); setEditId(null); fetchData(); }}
      />
    );
  }

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={() => setMode('new')}>{cfg.newLabel}</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {/* #767 Silme (iptal) onayı — diğer belge listeleriyle aynı desen. */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => { if (!deleting) setDeleteTarget(null); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Belgeyi İptal Et</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, fontSize: 14 }}>
                <strong>{deleteTarget.documentNo || displayDate(deleteTarget.documentDate)}</strong> belgesi iptal edilecek ve
                stok etkisi geri sarılacak. Bu işlem geri alınamaz. Devam edilsin mi?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete} disabled={deleting}>{deleting ? 'İptal ediliyor…' : 'Evet, İptal Et'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteTarget(null)} disabled={deleting}>Vazgeç</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default StockDocumentsListPage;
